import z from 'zod';
import { icoCountries } from './countryCodeUtils';
import { createDate, createDateTime } from './dateTimeUtils';

const iocCountryCodeErrorMessage = 'Country code must be a valid IOC country code';

export const iocCountryCodeSchema = z
    .string()
    .length(3, iocCountryCodeErrorMessage)
    .refine((val) => Object.keys(icoCountries).includes(val.toUpperCase()), {
        message: iocCountryCodeErrorMessage,
    });

export const iocCountryCodeSchemaNullish = z
    .string()
    .nullish()
    .refine((val) => val === null || val === undefined || Object.keys(icoCountries).includes(val.toUpperCase()), {
        message: iocCountryCodeErrorMessage,
    });

export const validateRequired = (value: string, fieldName: string): string | null => {
    return value.length < 1 ? `${fieldName} is required` : null;
};

export const validateMinLength = (value: string, minLength: number, fieldName: string): string | null => {
    return value.length < minLength ? `${fieldName} must be at least ${minLength} characters` : null;
};

export const validateExactLength = (value: string, exactLength: number, fieldName: string): string | null => {
    return value.length !== exactLength ? `${fieldName} must be exactly ${exactLength} characters` : null;
};

export const validateCountryCode = (value: string, required: boolean = true): string | null => {
    if (required && !value) {
        return 'Country code is required';
    }

    if (value.length > 0) {
        if (value.length !== 3) {
            return 'Country code must be exactly 3 characters';
        }

        const isValidCode = Object.keys(icoCountries).includes(value.toUpperCase());

        if (!isValidCode) {
            return 'Country code must be a valid IOC country code';
        }
    }

    return null;
};

export const validateTimeZoneId = (value: string, required: boolean = true): string | null => {
    if ((required && !value) || value.length < 1) {
        return 'Time zone is required';
    }

    const tzZones = Intl.supportedValuesOf('timeZone');

    if (!tzZones.includes(value)) {
        return 'Time zone must be a valid IANA time zone';
    }

    return null;
};

export const validateDateTime = (
    value: string | undefined,
    fieldName: string,
    required: boolean = true
): string | null => {
    if (required && (!value || value.length < 1)) {
        return `${fieldName} is required`;
    }

    if (value && value.length > 0) {
        const dateTime = createDateTime(value, `validateDateTime ${fieldName}`);
        if (!dateTime.isValid) {
            return `${fieldName} must be a valid date and time`;
        }
    }

    return null;
};

export const validateEndDateTimeAfterBegin = (
    endValue: string,
    beginValue: string,
    errorMessage: string = 'End date/time must be after begin date/time'
): string | null => {
    if (!endValue || !beginValue) {
        return null; // Let required validation handle empty values
    }

    const endDateTime = createDateTime(endValue, `validateEndDateTimeAfterBegin end`);
    const beginDateTime = createDateTime(beginValue, `validateEndDateTimeAfterBegin begin`);

    if (!endDateTime.isValid || !beginDateTime.isValid) {
        return null; // Let individual field validation handle invalid dates
    }

    if (endDateTime <= beginDateTime) {
        return errorMessage;
    }

    return null;
};

export const validateEnum = <T extends Record<string, string | number>>(
    value: string,
    enumObject: T,
    fieldName: string,
    required: boolean = true
): string | null => {
    if (required && (!value || value.length < 1)) {
        return `${fieldName} is required`;
    }

    if (value && value.length > 0) {
        const validValues = Object.values(enumObject);
        if (!validValues.includes(value as T[keyof T])) {
            return `Invalid ${fieldName.toLowerCase()}. Valid options: ${validValues.join(', ')}`;
        }
    }

    return null;
};

const normalizeFieldName = (fieldName: string | number | symbol): string => {
    return String(fieldName)
        .replace('Utc', '')
        .replace(/([A-Z])/g, ' $1') // Add space before capital letters
        .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
        .trim(); // Remove leading/trailing spaces
};

export const validateEndDateTime = <T>(
    value: string | undefined,
    beginFieldKey: keyof T,
    endFieldName: string,
    values: T,
    required: boolean = true
): string | null => {
    // Handle required/optional logic
    if (required && !value) {
        return `${endFieldName} is required`;
    }

    if (!value) {
        return null; // Optional field, no validation needed
    }

    // Validate datetime format
    const dateTimeValidation = validateDateTime(value, endFieldName, required);
    if (dateTimeValidation) {
        return dateTimeValidation;
    }

    // Validate end is after begin
    const beginValue = values[beginFieldKey];
    if (beginValue && typeof beginValue === 'string') {
        const beginFieldName = normalizeFieldName(beginFieldKey);

        return validateEndDateTimeAfterBegin(value, beginValue, `${endFieldName} must be after ${beginFieldName}`);
    }

    return null;
};

export const validateDate = (value: string, fieldName: string, required: boolean = true): string | null => {
    if (required && !value) {
        return `${fieldName} is required`;
    }

    if (!value) {
        return null; // Optional field, no validation needed
    }

    const date = createDate(value, `validateDate ${fieldName}`);
    if (!date.isValid) {
        return `${fieldName} must be a valid date`;
    }

    return null;
};

export const validateEndDateAfterBegin = (
    endValue: string,
    beginValue: string,
    errorMessage: string = 'End date must be after begin date'
): string | null => {
    if (!endValue || !beginValue) {
        return null; // Let individual field validation handle missing values
    }

    const endDate = createDate(endValue, `validateEndDateAfterBegin end`);
    const beginDate = createDate(beginValue, `validateEndDateAfterBegin begin`);

    if (!endDate.isValid || !beginDate.isValid) {
        return null; // Let individual field validation handle invalid dates
    }

    if (endDate <= beginDate) {
        return errorMessage;
    }

    return null;
};

export const validateEndDate = <T extends Record<string, unknown>>(
    value: string | undefined,
    beginFieldKey: keyof T,
    endFieldName: string,
    values: T,
    required: boolean = true
): string | null => {
    // Handle required/optional logic
    if (required && !value) {
        return `${endFieldName} is required`;
    }

    if (!value) {
        return null; // Optional field, no validation needed
    }

    // Validate date format
    const dateValidation = validateDate(value, endFieldName, required);
    if (dateValidation) {
        return dateValidation;
    }

    // Validate end is after begin
    const beginValue = values[beginFieldKey];
    if (beginValue && typeof beginValue === 'string') {
        const beginFieldName = normalizeFieldName(beginFieldKey);
        return validateEndDateAfterBegin(value, beginValue, `${endFieldName} must be after ${beginFieldName}`);
    }

    return null;
};

export const validateSchema = <T>(schema: z.ZodType<T>, object: T) => {
    const result = schema.safeParse(object);
    if (!result.success) return z.flattenError(result.error).fieldErrors;
    return {};
};

// export const validateSchema = <T>(schema: z.ZodType<T>, object: T): Record<string, string> => {
//     const result = schema.safeParse(object);
//     if (result.success) return {};

//     const errors: Record<string, string> = {};
//     for (const issue of result.error.issues) {
//         const path = issue.path.join('.'); // ["categories",0,"phases",1,"name"] -> "categories.0.phases.1.name"
//         if (!errors[path]) errors[path] = issue.message; // erster Fehler pro Feld gewinnt
//     }
//     return errors;
// };
